import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useCallback, useEffect, startTransition, useRef } from 'react'
import { getPastSessionResponses, getFocusGroupSessions, addSelfServiceRegistration, 
    getMembershipPlans, updatePlan, getMemberAgreement,
    getSelfServiceRegistrations, fetchCommunitySelfServices, getTeamMembers, 
    createPreference, submitUATResponse, inviteTeamMember, deleteTeamMember, updateTeamMember, UpdateTeamMemberPayload, verifyEmailInviteToken, addMemberAgreement, addCommunityPlans } from '../api/crud'
import { AccountSchema, CommunityAgreementSchema, FocusGroupSessionResponseSchema, InviteTeamMemberFormDataSchema, PlanSchema, PreferenceSchema, QuestionType, RegisteredPartner, SelfServiceDetailsMap, TeamMemberSchema, TeamRoleTypes, UATResponse } from '../data/types'
import { useResourceState } from '../contexts/kitchencontext'
import { useAuthState } from '../contexts/authcontext'
import {useLoadCommunity} from "../hooks/authhooks"; 
import { getCurrentTeamPermissions } from '../utils/teamPermissions' 
import moment from 'moment'
const staleTime = 1000
const isEnabled = true

/** Prefer submitted tier association fields when the API response is stale. */
const mergeMembershipPlanUpdateResponse = (
  response: PlanSchema,
  planUpdate: PlanSchema
): PlanSchema => ({
  ...response,
  ...(planUpdate.plan_type !== undefined ? { plan_type: planUpdate.plan_type } : {}),
  ...(planUpdate.associated_resources !== undefined
    ? { associated_resources: planUpdate.associated_resources }
    : {}),
  ...(planUpdate.features !== undefined ? { features: planUpdate.features } : {}),
});

type MembershipPlanFieldOverride = Pick<PlanSchema, "plan_type" | "associated_resources" | "features">;

const MEMBERSHIP_PLAN_OVERRIDES_STORAGE_KEY = "membershipPlanFieldOverrides";

const buildMembershipPlanOverrideKey = (communityId: string, planId: string) =>
  `${communityId}:${planId}`;

const loadMembershipPlanFieldOverrides = (): Map<string, MembershipPlanFieldOverride> => {
  if (typeof sessionStorage === "undefined") {
    return new Map();
  }

  try {
    const raw = sessionStorage.getItem(MEMBERSHIP_PLAN_OVERRIDES_STORAGE_KEY);
    if (!raw) {
      return new Map();
    }

    const parsed = JSON.parse(raw) as Record<string, MembershipPlanFieldOverride>;
    return new Map(Object.entries(parsed));
  } catch {
    return new Map();
  }
};

const persistMembershipPlanFieldOverrides = (
  overrides: Map<string, MembershipPlanFieldOverride>
) => {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(
      MEMBERSHIP_PLAN_OVERRIDES_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(overrides))
    );
  } catch {
    // Ignore storage quota or privacy-mode failures.
  }
};

const membershipPlanFieldOverrides = loadMembershipPlanFieldOverrides();

/** Clears persisted tier field overrides (for tests). */
export const clearMembershipPlanFieldOverrides = () => {
  membershipPlanFieldOverrides.clear();
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(MEMBERSHIP_PLAN_OVERRIDES_STORAGE_KEY);
  }
};

/**
 * Full association signature including linked resource ids and their bundled inventory
 * (id + quantity). Used to decide when an API payload finally matches a submitted
 * override so dual-plan inventory edits keep displaying the latest submitted values
 * until the backend actually persists them.
 */
const getAssociationsSignature = (associations: PlanSchema["associated_resources"] = []) =>
  (associations ?? [])
    .filter((association) => association.resource_id)
    .map((association) => {
      const inventory = (association.inventory ?? [])
        .map((selection) => `${selection.inventory_id ?? ""}:${selection.max_qty ?? 0}`)
        .sort()
        .join(",");
      return `${association.resource_id}#${inventory}`;
    })
    .sort()
    .join("|");

/** Remembers submitted tier fields until the API returns matching data. */
const setMembershipPlanFieldOverride = (
  communityId: string,
  planId: string,
  planUpdate: PlanSchema
) => {
  const override: MembershipPlanFieldOverride = {};

  if (planUpdate.plan_type !== undefined) {
    override.plan_type = planUpdate.plan_type;
  }
  if (planUpdate.associated_resources !== undefined) {
    override.associated_resources = planUpdate.associated_resources;
  }
  if (planUpdate.features !== undefined) {
    override.features = planUpdate.features;
  }

  if (Object.keys(override).length === 0) {
    return;
  }

  const overrideKey = buildMembershipPlanOverrideKey(communityId, planId);
  membershipPlanFieldOverrides.set(overrideKey, override);
  persistMembershipPlanFieldOverrides(membershipPlanFieldOverrides);
};

/**
 * Drops a persisted override for a plan id. Used when the backend returns a new
 * plan id for an updated plan (e.g. Stripe recreates the price on rate change), so
 * the override tied to the old id does not linger as an orphan.
 */
const removeMembershipPlanFieldOverride = (communityId: string, planId: string) => {
  const overrideKey = buildMembershipPlanOverrideKey(communityId, planId);
  if (membershipPlanFieldOverrides.delete(overrideKey)) {
    persistMembershipPlanFieldOverrides(membershipPlanFieldOverrides);
  }
};

/**
 * Replaces the plan matching the requested (possibly old) id with the merged
 * update. Handles the backend returning a new plan id by matching either the
 * requested id or the response id, and appends when no match exists.
 */
const applyPlanUpdateToList = (
  list: PlanSchema[] | undefined,
  requestedId: string,
  response: PlanSchema,
  planUpdate: PlanSchema
): PlanSchema[] => {
  const merged = mergeMembershipPlanUpdateResponse(response, planUpdate);
  const responseId = merged.plan_id ?? requestedId;
  const current = list ?? [];
  let replaced = false;
  const next = current.map((plan) => {
    if (plan.plan_id === requestedId || plan.plan_id === responseId) {
      replaced = true;
      return merged;
    }
    return plan;
  });
  if (!replaced) {
    next.push(merged);
  }
  return next;
};

/** Applies persisted tier field overrides over stale API plan payloads. */
export const applyMembershipPlanFieldOverrides = (
  communityId: string,
  plans: PlanSchema[]
): PlanSchema[] =>
  plans.map((plan) => {
    const planId = plan.plan_id ?? "";
    const overrideKey = buildMembershipPlanOverrideKey(communityId, planId);
    const override = membershipPlanFieldOverrides.get(overrideKey);

    if (!override) {
      return plan;
    }

    if (override.associated_resources !== undefined) {
      const apiSignature = getAssociationsSignature(plan.associated_resources);
      const overrideSignature = getAssociationsSignature(override.associated_resources);

      if (apiSignature === overrideSignature) {
        membershipPlanFieldOverrides.delete(overrideKey);
        persistMembershipPlanFieldOverrides(membershipPlanFieldOverrides);
        return plan;
      }
    }

    return mergeMembershipPlanUpdateResponse(plan, override);
  });

export const useLoadPlans = () => {
    let { communityId, setPlans } = useResourceState()
    const queryClient = useQueryClient()
    const plansSignatureRef = useRef("")
    const {data: plans, refetch: reloadPlans, isLoading: isLoadingPlans} = useQuery({ 
        queryKey: ["plans", communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching plans")
                const fetchedPlans = await getMembershipPlans(communityId) as PlanSchema[];
                return applyMembershipPlanFieldOverrides(communityId, fetchedPlans);
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })

    useEffect(() => {
        if (!plans || !setPlans) return
        const signature = (plans as PlanSchema[])
            .map((plan) => {
                const resourceIds = (plan.associated_resources || [])
                    .map((association) => association.resource_id ?? "")
                    .join(",")
                return `${plan.plan_id}:${plan.plan_type ?? "membership"}:${resourceIds}`
            })
            .join("|")
        if (signature === plansSignatureRef.current) return
        plansSignatureRef.current = signature
        const syncPlans = setPlans
        startTransition(() => syncPlans(plans))
    }, [plans, setPlans])

    // update resource plan
    const updatePlanMutation = useMutation({ 
        mutationFn: async ({planId, planUpdate}: {planId: string, planUpdate: PlanSchema}) => {
            console.log("update plan")
            const planType = planUpdate.plan_type || "membership"
            const res = await updatePlan(communityId ? communityId : "", planType, planId, planUpdate)
            return res
        },
        onSuccess: (res, { planId, planUpdate }) => {
            if (!communityId || !res) {
                return
            }

            // The backend can return a new plan id for an updated plan (Stripe recreates
            // the price when the rate changes). Use the response as the base so fresh
            // plan_name/plan_rate/plan_id surface immediately, keep submitted tier fields,
            // and re-key the override to the new id (clearing the stale old-id override).
            const merged = mergeMembershipPlanUpdateResponse(res, planUpdate)
            const newPlanId = merged.plan_id ?? planId
            if (newPlanId !== planId) {
                removeMembershipPlanFieldOverride(communityId, planId)
            }
            setMembershipPlanFieldOverride(communityId, newPlanId, planUpdate)

            queryClient.setQueryData<PlanSchema[]>(
                ["plans", communityId],
                (cached) => applyPlanUpdateToList(cached, planId, res, planUpdate)
            )

            const patchedPlans = queryClient.getQueryData<PlanSchema[]>(["plans", communityId])
            if (patchedPlans && setPlans) {
                const syncPlans = setPlans
                startTransition(() => syncPlans(patchedPlans))
            }
        }
    })


    const updateCommunityPlan = async (planId: string, planUpdate: PlanSchema) => {
        const result = await updatePlanMutation.mutateAsync({planId, planUpdate})
        return result ? true : false
    }

    return { isLoadingPlans, plans, reloadPlans, updateCommunityPlan}
}

/** Lightweight add-plan mutation for modals - avoids useResourceHook / useLoadPlans side effects. */
export const useAddCommunityPlan = (communityId: string) => {
    const queryClient = useQueryClient()
    const addPlanMutation = useMutation({
        mutationFn: async (plan: PlanSchema) => {
            return addCommunityPlans(communityId, plan)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["plans", communityId] })
        },
    })

    const addCommunityPlan = async (plan: PlanSchema) => {
        try {
            await addPlanMutation.mutateAsync(plan)
            return true
        } catch {
            return false
        }
    }

    return {
        addCommunityPlan,
        isLoading: addPlanMutation.isPending,
        isError: addPlanMutation.isError,
    }
}

/** Fetches the saved member agreement for a community (returns object with presigned url for .docx). */
export const useGetMemberAgreement = (communityId: string) => {
    const {
        data: agreement, isLoading: agreementLoading, isError: IsAgreementError, error: AgreementError, refetch: reloadAgreement
    } = useQuery({
        queryKey: ['agreement', communityId],
        queryFn: async () => { const res = await getMemberAgreement(communityId); return res; },
    });

    const addMemberAgreementMutation = useMutation({
        mutationFn: async (agreement: CommunityAgreementSchema) => {
            return await addMemberAgreement(communityId as string, agreement)
        }
    })

    const addMemberAgreementData = async (agreement: CommunityAgreementSchema) => {
        const result = await addMemberAgreementMutation.mutateAsync(agreement);
        return result ? true : false;
    }

    return { agreement, agreementLoading, IsAgreementError, AgreementError, reloadAgreement, addMemberAgreementData };
}

export const useManageTeam = () => {
    var { communityId } = useResourceState()
    const queryClient = useQueryClient()
    const {
        data: team,
        isLoading,
        refetch: reloadTeam,
        isError,
        error
    } = useQuery({
        queryKey: ['team', communityId],
        enabled: Boolean(communityId),
        placeholderData: (previousData) => previousData,
        queryFn: async () => {
            const response = await getTeamMembers(communityId as string);
            return response ? response : []
        }
    });

    const invalidateTeam = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['team', communityId] })
    }, [communityId, queryClient])

    const inviteTeamMemberMutation = useMutation({
        mutationFn: async (teamMember: InviteTeamMemberFormDataSchema) => {
            return await inviteTeamMember(communityId as string, teamMember)
        },
        onSuccess: () => {
            void invalidateTeam()
        }
    })

    const deleteTeamMemberMutation = useMutation({
        mutationFn: async (email: string) => {
            return await deleteTeamMember(communityId as string, email)
        },
        onSuccess: () => {
            void invalidateTeam()
        }
    })

    const updateTeamMemberMutation = useMutation({
        mutationFn: async (payload: UpdateTeamMemberPayload) => {
            return await updateTeamMember(communityId as string, payload)
        },
        onSuccess: (updated) => {
            queryClient.setQueryData(['team', communityId], (current: TeamMemberSchema[] | undefined) => {
                if (!Array.isArray(current) || !updated) {
                    return current
                }
                const updatedEmail = String((updated as TeamMemberSchema).email || "").trim().toLowerCase()
                const merged = current.map((member) =>
                    String(member.email || "").trim().toLowerCase() === updatedEmail
                        ? { ...member, ...(updated as TeamMemberSchema) }
                        : member
                )
                if (!merged.some((member) => String(member.email || "").trim().toLowerCase() === updatedEmail)) {
                    return [...merged, updated as TeamMemberSchema]
                }
                return merged
            })
            void invalidateTeam()
        }
    })

    const inviteTeamMemberData = async (teamMember: InviteTeamMemberFormDataSchema) => {    
        try {
            const result = await inviteTeamMemberMutation.mutateAsync(teamMember);
            return result;
        } catch {
            return null;
        }
    }

    const deleteTeamMemberData = async (email: string) => {
        try {
            return await deleteTeamMemberMutation.mutateAsync(email);
        } catch {
            return null;
        }
    }

    const updateTeamMemberData = async (payload: UpdateTeamMemberPayload) => {
        try {
            return await updateTeamMemberMutation.mutateAsync(payload);
        } catch {
            return null;
        }
    }

    const verifyUserEmailMutation = useMutation({
        mutationFn: async ({primaryCommunityId, email, token}: {primaryCommunityId: string, email: string, token: string}) => {
            return await verifyEmailInviteToken(primaryCommunityId as string, email, token)
        }
    })

    const verifyUserEmail = useCallback(async ({primaryCommunityId, email, token}: {primaryCommunityId: string, email: string, token: string}) => {
        const result = await verifyUserEmailMutation.mutateAsync({primaryCommunityId: primaryCommunityId as string, email, token});
        return result;
    }, [verifyUserEmailMutation])

    const getAccountRole = (account: AccountSchema): TeamRoleTypes => {
        if (team) {
            for (const teamMember of team as TeamMemberSchema[]) {
                if (teamMember.email === account.email) {
                    return teamMember.role
                }
            }
        }
        return "viewer"
    }
    return {
        team,
        isLoading,
        isError,
        error,
        reloadTeam,
        inviteTeamMemberData,
        deleteTeamMemberData,
        updateTeamMemberData,
        getAccountRole,
        verifyUserEmail,
    }
}

/** Signed-in partner community permissions for route and sidebar gating. */
export const useTeamAccess = () => {
    const { team, isLoading } = useManageTeam()
    const { authUser } = useAuthState()
    const permissions = useMemo(
        () => getCurrentTeamPermissions(team as TeamMemberSchema[] | undefined, authUser),
        [team, authUser]
    )
    return { permissions, isTeamLoading: isLoading, team }
}

export const useFocusGroupCommunitySessions = () => {
    var { communityId } = useResourceState()
    const {
        data: focusGroupSessions,
        isLoading: isLoadingFocusGroupSessions,
        isError: isErrorFocusGroupSessions,
        error: errorFocusGroupSessions
    } = useQuery({
        queryKey: ['focusGroupSessions', communityId],
        queryFn: async () => {
            const response = await getFocusGroupSessions(communityId as string);
            return response ? response : []
        }
    });

    return {
        focusGroupSessions,
        isLoadingFocusGroupSessions,
        isErrorFocusGroupSessions,
        errorFocusGroupSessions
    };  
};

export const useFocusGroupSessions = () => {
    const { focusGroupSessions } = useFocusGroupCommunitySessions();
    const formattedFocusGroupSessions = useMemo(() => 
        focusGroupSessions ? focusGroupSessions.map(session => ({
            ...session,
            sessionDate: moment(session.sessionDate),
            sections: session.sections.map(section => ({
              ...section,
              questions: section.questions.map(question => ({
                ...question,
                type: question.type as QuestionType
              }))
            }))
          })) : [],
        [focusGroupSessions]
    );
    return {formattedFocusGroupSessions};
}


export const useFocusGroupSessionResponses = (sessionId: string) => {
    var { communityId } = useResourceState()
    const { 
        data: pastSessionResponses, 
        isLoading: isLoadingPastSessionResponse, 
        isError: isErrorPastSessionResponse, 
        error: errorPastSessionResponse, 
        refetch: reloadPastSessionResponse 
    } = useQuery({
        queryKey: ['pastSessionResponses', communityId, sessionId],
        queryFn: async () => {
            return await getPastSessionResponses(communityId as string, sessionId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });


    const isLoading = isLoadingPastSessionResponse 
    const isError = isErrorPastSessionResponse 
    const error = errorPastSessionResponse

    return {
        pastSessionResponses: pastSessionResponses as FocusGroupSessionResponseSchema[],
        isLoading,
        isError,
        error,
        reloadPastSessionResponse
    };
};


export const useSelfServices = () => {
    var { communityId } = useResourceState()
    const { 
        data: selfServices, 
        isLoading: isLoadingSelfServices, 
        isError: isErrorSelfServices, 
        error: errorSelfServices,
        refetch: reloadSelfServices
    } = useQuery({
        queryKey: ['selfService', communityId],
        queryFn: async () => {
            const response = await fetchCommunitySelfServices(communityId as string);
            if (response){
                return response
            }
            return {}
        },
        // staleTime: 5 * 60 * 1000, // 5 minutes
    });
    return {
        selfServices: selfServices as SelfServiceDetailsMap,
        isLoadingSelfServices,
        isErrorSelfServices,
        errorSelfServices,
        reloadSelfServices
    }   
}


export const useSelfServiceRegistration = (eventCode: string) => {

    var { communityId } = useResourceState()

    const { 
        data: selfServiceRegistrations, 
        isLoading: isLoadingSelfServiceRegistrations, 
        isError: isErrorSelfServiceRegistrations, 
        error: errorSelfServiceRegistrations,
        refetch: reloadSelfServiceRegistrations
    } = useQuery({
        queryKey: ['selfServiceRegistration', communityId, eventCode],
        queryFn: async () => {
            return await getSelfServiceRegistrations(communityId as string, eventCode);
        }
    });

    const addSelfServiceMutation = useMutation({ 
        mutationFn: async ({communityId, selfServiceRegistration}: {communityId: string, selfServiceRegistration: RegisteredPartner}) => {
            console.log("add self service registration") 
            const res = await addSelfServiceRegistration(communityId, selfServiceRegistration)
            return res
        },
        onSuccess: (data) => {
            console.log("add self service registration success", data);
            reloadSelfServiceRegistrations()
        },
        onError: (error) => {
            console.log("add self service registration error", error);
        }
    })

    const addSelfServiceRegistrationData = async (communityId: string, selfServiceRegistration: RegisteredPartner) => {
        const result = await addSelfServiceMutation.mutateAsync({communityId: communityId as string, selfServiceRegistration});
        return result ? true : false;
    }

    return {
        selfServiceRegistrations,
        isLoading: isLoadingSelfServiceRegistrations || addSelfServiceMutation.isPending,
        isError: isErrorSelfServiceRegistrations,
        error: errorSelfServiceRegistrations,
        reloadSelfServiceRegistrations,
        addSelfServiceRegistrationData
    };
};


export const useUpdatePreferences = () => {
    var { communityId } = useResourceState()
    var { reloadCommunity } = useLoadCommunity(communityId as string)
    
    const updatePreferencesMutation = useMutation({ 
        mutationFn: async (preferences: PreferenceSchema) => {
            console.log("create / update preferences") 
            const res = await createPreference(communityId as string, preferences)
            reloadCommunity()
            return res
    }})

    const updatePreferencesData = async (preferences: PreferenceSchema) => {
        updatePreferencesMutation.mutate(preferences);
        return updatePreferencesMutation.data ? true : false
    }   

    const isLoading = updatePreferencesMutation.isPending   
    const isError = updatePreferencesMutation.isError
    const error = updatePreferencesMutation.error
    const isSuccess = updatePreferencesMutation.isSuccess
    
    return {
        updatePreferencesData,
        isLoading,
        isError,
        error,
        isSuccess
    }
}


export const useSubmitUATResponse = () => {
        
    const submitUATResponseMutation = useMutation({
        mutationFn: async (data: {email_address: string, uatResponse: UATResponse}) => {
            const res = await submitUATResponse(data.email_address, data.uatResponse)
            return res
        }
    })

    const submitUATResponseData = async (data: {email_address: string, uatResponse: UATResponse}) => {
        submitUATResponseMutation.mutate(data);
        return submitUATResponseMutation.data ? true : false
    }

    const isLoading = submitUATResponseMutation.isPending
    const isError = submitUATResponseMutation.isError
    const error = submitUATResponseMutation.error
    const isSuccess = submitUATResponseMutation.isSuccess

    return {
        submitUATResponseData, 
        isLoading,
        isError,
        error,
        isSuccess
    }
}


// export const useCommunityPreferences = () => {
//     var { communityId } = useResourceState()
//     const {
//         data: preferences,
//         isLoading: isLoadingPreferences,
//         isError: isErrorPreferences,
//         error: errorPreferences,
//         refetch: reloadPreferences
//     } = useQuery({
//         queryKey: ['preferences', communityId],
//         queryFn: async () => {
//             return await getPreferences({communityId: communityId as string})
//         }
//     });

//     return {
//         preferences,
//         isLoadingPreferences,
//         isErrorPreferences,
//         errorPreferences,
//         reloadPreferences
//     }
// }